import { msg } from "@lingui/core/macro";
import {
  clampCell,
  FLOOR_GRID_COLS,
  FLOOR_GRID_ROWS,
  type FloorCell,
  pointToCell,
  resolveFloorLayout,
} from "@comandero/shared";
import type { DiningTable } from "@comandero/powersync";
import { createMemo, createSignal, Index } from "solid-js";
import { useI18n } from "@/providers/i18n-provider";

interface FloorMapProps {
  tables: () => DiningTable[];
  /** Edit mode: drag (pointer) or move with arrow keys (keyboard). */
  editing: () => boolean;
  /** Persists a table's cell (drop from the drag or keyboard arrow). */
  onMove: (tableId: string, cell: FloorCell) => void;
  /** Tap on a table OUTSIDE edit mode (open/serve). */
  onSelect?: (table: DiningTable) => void;
}

interface DragState {
  id: string;
  /** Visual delta in pixels relative to the source cell. */
  dx: number;
  dy: number;
  startX: number;
  startY: number;
}

/**
 * Dining-room floor plan: fixed grid (12×8 cells) where each table lives at
 * its `pos_x/pos_y`; unplaced tables are auto-distributed into free cells
 * (`resolveFloorLayout`, pure). In edit mode each table is dragged with
 * pointer events (native drag, no libraries) or moved by keyboard with the
 * arrow keys — each movement persists the cell via `onMove` and syncs to the
 * other terminals like any other local-first mutation.
 */
export function FloorMap(props: FloorMapProps) {
  const { t } = useI18n();
  let containerRef: HTMLDivElement | undefined;
  const [drag, setDrag] = createSignal<DragState | null>(null);

  const layout = createMemo(() => resolveFloorLayout(props.tables()));

  const cellStyle = (cell: FloorCell) => ({
    left: `${(cell.x / FLOOR_GRID_COLS) * 100}%`,
    top: `${(cell.y / FLOOR_GRID_ROWS) * 100}%`,
    width: `${100 / FLOOR_GRID_COLS}%`,
    height: `${100 / FLOOR_GRID_ROWS}%`,
  });

  function onPointerDown(
    e: PointerEvent & { currentTarget: HTMLButtonElement },
    table: DiningTable,
  ) {
    if (!props.editing()) {
      return;
    }
    e.currentTarget.setPointerCapture(e.pointerId);
    setDrag({ id: table.id, dx: 0, dy: 0, startX: e.clientX, startY: e.clientY });
  }

  function onPointerMove(e: PointerEvent) {
    const d = drag();
    if (!d) {
      return;
    }
    setDrag({ ...d, dx: e.clientX - d.startX, dy: e.clientY - d.startY });
  }

  function onPointerUp(e: PointerEvent, table: DiningTable) {
    const d = drag();
    if (!d || d.id !== table.id) {
      return;
    }
    setDrag(null);
    if (!containerRef) {
      return;
    }
    const rect = containerRef.getBoundingClientRect();
    const cell = pointToCell(e.clientX - rect.left, e.clientY - rect.top, rect.width, rect.height);
    const origin = layout().get(table.id);
    if (origin && origin.x === cell.x && origin.y === cell.y) {
      return; // no net movement: do not pollute the CRUD queue
    }
    props.onMove(table.id, cell);
  }

  function onKeyDown(e: KeyboardEvent, table: DiningTable) {
    if (!props.editing()) {
      return;
    }
    const delta: Record<string, [number, number]> = {
      ArrowLeft: [-1, 0],
      ArrowRight: [1, 0],
      ArrowUp: [0, -1],
      ArrowDown: [0, 1],
    };
    const move = delta[e.key];
    if (!move) {
      return;
    }
    e.preventDefault();
    const origin = layout().get(table.id) ?? { x: 0, y: 0 };
    const next = clampCell(origin.x + move[0], origin.y + move[1]);
    if (next.x !== origin.x || next.y !== origin.y) {
      props.onMove(table.id, next);
    }
  }

  return (
    <div
      ref={containerRef}
      class="relative aspect-[3/2] w-full touch-none overflow-hidden rounded-lg border border-border/40 bg-muted/30"
      data-testid="floor-map"
      data-editing={props.editing() ? "true" : "false"}
      role="group"
      aria-label={t(msg`Floor plan`)}
    >
      {/* `Index` (no `For`): la live query entrega un ARRAY NUEVO en cada
          refresh; con `For` cada tile se re-crearía y el foco del teclado se
          perdería tras cada movimiento. `Index` reutiliza los nodos DOM. */}
      <Index each={props.tables()}>
        {(table) => {
          const cell = () => layout().get(table().id) ?? { x: 0, y: 0 };
          const dragging = () => drag()?.id === table().id;
          return (
            <button
              type="button"
              class="absolute p-0.5 focus-visible:z-10"
              classList={{
                "cursor-move": props.editing(),
                "z-20": dragging(),
                "transition-[left,top] duration-150 motion-reduce:transition-none": !dragging(),
              }}
              style={{
                ...cellStyle(cell()),
                transform: dragging() ? `translate(${drag()!.dx}px, ${drag()!.dy}px)` : undefined,
              }}
              data-testid="floor-map-table"
              data-table-name={table().name}
              data-table-status={table().status}
              data-cell={`${cell().x},${cell().y}`}
              aria-label={
                props.editing()
                  ? `${t(msg`Move table`)} ${table().name ?? ""}`
                  : (table().name ?? undefined)
              }
              onPointerDown={(e) => onPointerDown(e, table())}
              onPointerMove={onPointerMove}
              onPointerUp={(e) => onPointerUp(e, table())}
              onKeyDown={(e) => onKeyDown(e, table())}
              onClick={() => {
                if (!props.editing()) {
                  props.onSelect?.(table());
                }
              }}
            >
              <span
                class="flex h-full w-full select-none flex-col items-center justify-center rounded-md border text-[0.65rem] font-medium leading-tight"
                classList={{
                  "border-green-600/50 bg-green-500/15 text-green-700 dark:text-green-400":
                    table().status === "free",
                  "border-orange-500/50 bg-orange-500/15 text-orange-600 dark:text-orange-400":
                    table().status !== "free",
                  "ring-2 ring-accent": dragging(),
                }}
              >
                {table().name}
              </span>
            </button>
          );
        }}
      </Index>
    </div>
  );
}
